import React from 'react';
import { Box, ButtonBase, Grid, Typography } from '@mui/material';
import { Button } from '@components/button/Button';
import { RegistrationRole } from '@pages/Login/constants';
import { useNavigate } from 'react-router-dom';
import { RegistrationContactFooter } from '@pages/Login/SignUpView/RegistrationContactFooter';
import { BROKER_PARTNER_ID_REQUIRED_MESSAGE } from '@pages/Login/SignUpView/registrationContent';
import { signUpContainerSx } from '@pages/Login/SignUpView/SignUpView.styles';

const ROLES: { role: RegistrationRole; title: string; description: React.ReactNode }[] = [
  {
    role: RegistrationRole.BROKER_PRODUCER,
    title: 'Broker / Producer',
    description: (
      <>
        For members of LIO appointed brokerages that wish to get quotes and manage client policies.
        <Box component='span' sx={{ fontWeight: 700, display: 'block' }}>
          {BROKER_PARTNER_ID_REQUIRED_MESSAGE}
        </Box>
      </>
    ),
  },
  {
    role: RegistrationRole.POLICYHOLDER,
    title: 'Policyholder / Insured',
    description: 'Manage your account or make payments.',
  },
  {
    role: RegistrationRole.BILLING_CONTACT,
    title: 'Billing Contact or Agency Payer',
    description: 'Make payments for a policy on behalf of an appointed agency or a LIO policyholder.',
  },
];

export const RegistrationRoleSelectionView = ({ onRoleSelect }: { onRoleSelect: (role: RegistrationRole) => void }) => {
  const navigate = useNavigate();

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
      <Grid container sx={[signUpContainerSx, { color: 'text.primary' }]}>
        <Grid size={{ xs: 12 }} sx={{ mb: 0.5, textAlign: 'center' }}>
          <Typography variant='h5'>Create your LIO account</Typography>
        </Grid>
        <Grid size={{ xs: 12 }} sx={{ mb: 2, textAlign: 'center' }}>
          <Typography variant='bodySmall' sx={{ color: 'text.caption', mt: 2, display: 'block' }}>
            Select the role that fits you best:
          </Typography>
        </Grid>
        <Grid size={{ xs: 12 }}>
          {ROLES.map(({ role, title, description }) => (
            <ButtonBase key={role} onClick={() => onRoleSelect(role)} sx={sxProps.roleCard}>
              <Typography
                variant='body'
                sx={{ color: 'secondary.main', fontWeight: 600, mb: 0.5, textAlign: 'left', display: 'block' }}
              >
                {title}
              </Typography>
              <Typography
                variant='body2'
                sx={{
                  color: 'text.caption',
                  textAlign: 'left',
                  lineHeight: 1.5,
                  display: 'block',
                  fontWeight: 'normal',
                }}
              >
                {description}
              </Typography>
            </ButtonBase>
          ))}
        </Grid>
        <Grid size={{ xs: 12 }} sx={{ mt: 2, textAlign: 'center' }}>
          <Button variant='text' disableRipple onClick={() => navigate('/login')}>
            Back to sign in
          </Button>
        </Grid>
      </Grid>
      <RegistrationContactFooter />
    </Box>
  );
};

const sxProps = {
  roleCard: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    width: '100%',
    backgroundColor: 'rgba(255,255,255,0.07)',
    borderRadius: '4px',
    padding: '12px 16px',
    mb: 1.4,
    transition: 'background-color 0.15s ease',
    '&:hover': {
      backgroundColor: 'rgba(255,255,255,0.14)',
    },
  },
};
